import { css } from 'aphrodite';
import * as React from 'react';
import { NavLink } from 'react-router-dom';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../models/Logging';
import { baseStyleSheet } from '../../../styles/styles';
import { campaignSessionStore } from './campaignSessionStore';
import {
	getDailySendLimit,
	getEstimatedDaysToComplete,
	getNumberOfEmailsToSend,
	getRecipientInfo,
} from './resourceHelpers';
import { s } from './styles';
import { IAIScheduleSuccessProps } from './types';

interface DetailMessageProps {
	props: IAIScheduleSuccessProps;
	primaryResourceType?: string;
}

export const DetailMessage: React.FC<DetailMessageProps> = ({ props, primaryResourceType }) => {
	const { resourceType, requiresCompliance, multiple } = props;
	const displayResourceType = primaryResourceType || resourceType;
	const userSession = useUserSession();
	const { logInput } = useEventLogging('AIScheduleSuccess');

	// Only use resource-specific data if it matches the displayed resource type
	const relevantEmailComposer =
		displayResourceType === 'email' && resourceType === 'email' ? props.emailComposer : null;
	const recipientInfo = getRecipientInfo(relevantEmailComposer);

	const onReportsLinkClicked = React.useCallback(
		(e: React.MouseEvent<HTMLAnchorElement>) => {
			const { onNavigateToReports, onNavigateToReportingGroupEmail } = props;
			logInput('ReportsLink', 'Click');
			campaignSessionStore.clearSession();
			const handler = onNavigateToReports ?? onNavigateToReportingGroupEmail;
			if (handler) {
				handler(e);
			}
		},
		[props, logInput]
	);

	const getCampaignDisplayName = React.useCallback((): string => {
		let name = 'campaign';
		switch (displayResourceType) {
			case 'blog':
				name = 'blog post';
				break;
			case 'social':
				name = 'social media post';
				break;
			case 'email':
				name = 'email';
				break;
			default:
				break;
		}
		return `${name}${multiple ? 's' : ''}`;
	}, [displayResourceType, multiple]);

	const renderComplianceMessage = (): React.ReactNode => {
		if (!requiresCompliance) {
			return null;
		}

		const campaignDisplayName = getCampaignDisplayName();
		return (
			<div className={css(s.detailMessage)}>
				{`We've emailed you a copy of the ${campaignDisplayName}. ${multiple ? 'These' : 'This'} ${campaignDisplayName} ${multiple ? 'are' : 'is'} now pending. You can come back and mark ${multiple ? 'these' : 'this'} ${campaignDisplayName} as "Approved" later.`}
			</div>
		);
	};

	const renderEmailDetails = (): React.ReactNode => {
		const { estimate } = props;
		const relevantEstimate = displayResourceType === 'email' && resourceType === 'email' ? estimate : null;
		const numberOfEmailsToSend = getNumberOfEmailsToSend(relevantEmailComposer);
		const estimatedDaysToComplete = getEstimatedDaysToComplete(props, relevantEmailComposer);
		const dailySendLimit = getDailySendLimit(props, relevantEmailComposer);

		const scheduleInfo =
			relevantEstimate && estimatedDaysToComplete > 0 ? (
				<span>
					{dailySendLimit && (
						<span>{` Your email server has a daily limit of ${dailySendLimit.count} emails per day. `}</span>
					)}
					{`Levitate will send the ${numberOfEmailsToSend} email${
						numberOfEmailsToSend > 1 ? 's' : ''
					} over ${estimatedDaysToComplete} day${estimatedDaysToComplete > 1 ? 's' : ''}.`}
				</span>
			) : null;

		return (
			<div className={css(s.detailMessage)}>
				<div>
					{userSession?.account?.features?.htmlNewsletter?.useClientEmailProvider ? scheduleInfo : null}
					You can view the queued up email(s) in&nbsp;
					<NavLink
						className={css(baseStyleSheet.brandLink, s.link)}
						onClick={onReportsLinkClicked}
						to='/reporting/group-email'
					>
						<span>Group Email Reports</span>
					</NavLink>
					.
				</div>
			</div>
		);
	};

	const renderSocialDetails = (): React.ReactNode => {
		return (
			<div className={css(s.detailMessage)}>
				You can view the queued up post in &nbsp;
				<NavLink
					className={css(baseStyleSheet.brandLink, s.link)}
					onClick={onReportsLinkClicked}
					to='/reporting/social-media-posts'
				>
					<span>Social Media Reports</span>
				</NavLink>
				.
			</div>
		);
	};

	const renderBlogDetails = (): React.ReactNode => {
		return (
			<div className={css(s.detailMessage)}>
				You can view the queued up blog post in the &nbsp;
				<NavLink
					className={css(baseStyleSheet.brandLink, s.link)}
					onClick={onReportsLinkClicked}
					to='/content-calendar'
				>
					<span>Content Calendar</span>
				</NavLink>
			</div>
		);
	};

	const renderWhoWillBeNotified = (): React.ReactNode => {
		if (displayResourceType !== 'email' || !recipientInfo) {
			return null;
		}

		return <div className={css(s.titleMessage, s.whoWillBeNotified)}>{recipientInfo}</div>;
	};

	const renderDetailMessage = (): React.ReactNode => {
		switch (displayResourceType) {
			case 'email':
				return renderEmailDetails();
			case 'social':
				return renderSocialDetails();
			case 'blog':
				return renderBlogDetails();
			default:
				return null;
		}
	};

	return (
		<>
			{renderWhoWillBeNotified()}
			{renderDetailMessage()}
			{renderComplianceMessage()}
		</>
	);
};
